<script setup lang="ts">
import i18n from '@/lang/i18n';
import ModalFlashMessage from "@/components/Common/ModalFlashMessage.vue";
import PaginateWithCount from "@/components/PaginateWithCount.vue";
import { Ref, ref, computed, onMounted, watch } from "vue"
import { GDialog } from 'gitart-vue-dialog'
import { formatTimeFromSeconds, formatDateTimeJP, formatTime } from "@/services/functions"
import Api from "@/services/api/ApiServiceFabrick";

const emits = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const props = defineProps<{
  modelValue: boolean,
  deliveryPlanId: string
  createAuthority: boolean
  showFlash: (message: string, show: boolean, type: string, fadeout: boolean) => void
}>()

const modelValue = computed(() => {
  return props.modelValue;
})

const currentPage:Ref<number> = ref(1)
const totalPage:Ref<number> = ref(0)

const longtimeContinuousWarnings = ref([])
onMounted(() => {
  getAlertStatuses()
})

watch(() => currentPage.value,
() => {
  getAlertStatuses()
})

function getAlertStatuses() {
  alertsLoading.value = true
  longtimeContinuousWarnings.value = []
  Api().getLongTimeContinuousAlerts({
    delivery_plan_id: props.deliveryPlanId,
    page: currentPage.value,
  }).then((res) => {
    currentPage.value = res.current_page
    totalPage.value = Math.ceil(res.total / res.per_page)
    longtimeContinuousWarnings.value = res.data
    alertsLoading.value = false
  })
}

const loading:Ref<boolean> = ref(false)
const alertsLoading:Ref<boolean> = ref(true)

function closeModal() {
  emits('cancel')
}

const viewTimes = computed(() => {
  let ret = [];
  longtimeContinuousWarnings.value.forEach((arr) => {
    const start = new Date(arr.start_time);
    const end = new Date(arr.end_time);
    const diffSeconds = (arr.time_range);
    const range = formatTimeFromSeconds(diffSeconds)
    ret.push({
      start: formatDateTimeJP(start),
      end: formatTime(end),
      range: range,
      diffSeconds: diffSeconds,
      is_extension: arr.is_extension
    })
  })
  return ret;
})

function ignoreAlert(index: number) {
  longtimeContinuousWarnings.value[index].is_extension = true
}

function requestChangeStatus(index: number) {
  loading.value = true
  Api().updateLongTimeContinuousAlerts(longtimeContinuousWarnings.value).then((data) => {
    loading.value = false
    props.showFlash(i18n.global.t('pages.common.messages.successUpdate'), true, "info", true)
    closeModal()
  }).catch(() => {
    loading.value = false
    flashMessage.value = {
      message: i18n.global.t('pages.common.messages.failedUpdate'),
      show: true,
      type: "error",
      cancel: false
    }
  })
}

const flashMessage = ref({
  message:"",
  show: false,
  type: "info",
  cancel: true
})

</script>
<template>
  <GDialog
    v-model="modelValue"
    max-width="50%"
    transition="custom-from-bottom-transition"
    :persistent='true'
  >
    <ModalFlashMessage
      :message = "flashMessage.message"
      :show = "flashMessage.show"
      :type = "flashMessage.type"
      :cancel = "flashMessage.cancel"
      @setShow = "(show) => {flashMessage.show = show}"
    ></ModalFlashMessage>
    <div class="modal-header ml-5 mt-8">
      <label :class="props.titleClass" class="h4 m-0">
        {{ i18n.global.t('pages.dailyReports.ignoreLongtimeContinuousWarning.updateModalTitle') }}
      </label>
    </div>

    <div class="modal-body mx-4">
      <slot name="supplementary-item" />

      <div :class="props.noteClass">
        <label v-if="!alertsLoading && viewTimes.length == 0" class="mx-5 my-3 h5 empty-alerts">
          {{ i18n.global.t('pages.dailyReports.ignoreLongtimeContinuousWarning.emptyAlerts') }}
        </label>
        <div class="longtime-continuous-warning mb-2">
          <table class="mx-5 my-3 h5 longtime-continuous-warning-table">
            <tbody>
              <tr v-for="(warning, index) in viewTimes" v-bind:key="'warning'+index">
                <td>{{warning.start}}&nbsp;~&nbsp;{{warning.end}}&nbsp;({{warning.range}})</td>
                <td>
                  <v-btn :disabled="true" v-if="warning.is_extension">
                    {{ i18n.global.t('pages.dailyReports.ignoreLongtimeContinuousWarning.ignoredBtn') }}
                  </v-btn>
                  <v-btn v-else @click="ignoreAlert(index)">
                    {{ i18n.global.t('pages.dailyReports.ignoreLongtimeContinuousWarning.ignoreBtn') }}
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <PaginateWithCount class="justify-end" :totalPage="totalPage" v-model="currentPage" :showItemCount="false" />
      </div>
    </div>

    <div class="d-flex justify-content-end m-3 mt-4">
      <v-btn class="ma-2 reset-btn" @click="closeModal">
        {{ i18n.global.t('common.backBtn') }}
      </v-btn>
      <v-btn
        v-if="viewTimes.length != 0 && props.createAuthority"
        class="ma-2 exec-btn"
        :class="props.confirmBtnClass"
        @click="requestChangeStatus"
        :loading="loading"
      >
        {{ i18n.global.t('common.saveBtn') }}
      </v-btn>
    </div>
  </GDialog>
</template>

<style scoped>
.longtime-continuous-warning {
  overflow: auto;
  max-height: 40vh;
}
.longtime-continuous-warning-table tr>*,
.empty-alerts {
  padding-top: 0.5vw !important;
  padding-bottom: 0.5vw !important;
}
.longtime-continuous-warning-table td {
  padding-right: 4vw !important;
}
</style>
